// Display copy for Free and Plus: a 30-day trial with everything, then Free (the
// shared list, up to 15 open items) or Plus for up to six people, billed yearly
// or monthly (pricing spec, revised 2026-10-15).
//
// Stripe is the authority on what is actually charged, and the Prices are
// multi-currency, so the PWA keeps no copy of the amounts of its own:
// hooks/usePrices.ts fetches them from `GET /billing/prices` (which reads the
// Stripe Prices, docs/OPERATIONS.md › Stripe billing) as a table of minor units
// per currency, and the helpers here turn one currency's row into the strings
// the subscribe sheet, Profile and the login page render — in the viewer's own
// currency and locale (lib/currency.ts), which is also the currency Checkout is
// then told to charge in. FALLBACK_PRICES is the one remaining hand copy, used
// only while the route is unreachable.
//
// Deliberately excludes tax: Stripe is the merchant of record (Managed
// Payments, billing/handler.ts), so Checkout adds the buyer's own VAT/sales tax
// on top and shows a total we can't know here — hence "plus VAT where it
// applies" as the caveat the UI renders alongside these (unless the Dashboard
// marked that currency's amount tax-inclusive, which the table reports).
//
// Annual is the plan we lead with. Stripe's fixed per-charge fee is levied
// twelve times on a monthly plan and once on a yearly one, and the launch
// offers only ever discount the first year.

import { formatMoney } from './currency'

export type PlanId = 'annual' | 'monthly'

/** The paid plan's name, in the app and on the landing page. */
export const PLAN_NAME = 'Plus'

/** Length of the no-card trial every new household gets — the column default in 042_free_plus.sql. */
export const TRIAL_DAYS = 30

/** Plus this many days past the paid-through date — household_access() in 042. */
export const GRACE_DAYS = 3

/**
 * Open (unticked) items a free list holds. Mirror of free_item_limit() in
 * 042_free_plus.sql — its trigger enforces it (PT413); this copy drives the
 * counter and the full bar. household_plan.test.sql pins the SQL side.
 */
export const FREE_ITEM_LIMIT = 15

/**
 * People per household, Free or Plus. Mirror of max_members() in
 * 031_household_plan.sql — the seat guard trigger enforces it; this copy drives
 * the "2 of 6 people" counter. Change both together (members.test.sql pins
 * the SQL side, pricing.test.ts this one).
 */
export const MAX_MEMBERS = 6

export interface LaunchOffer {
  code: string
  percentOff: number
  /** At most this many households get it (the code's max redemptions in Stripe). */
  households: number
  /** For this many days after launch (the code's expiry in Stripe). */
  days: number
}

/**
 * The founding offer the app names, or null once it has ended. Display only:
 * the billing function applies the code itself (STRIPE_LAUNCH_PROMO_CODE), and
 * the discount, its cap, its window and the yearly-only rule all live on the
 * code in Stripe (docs/OPERATIONS.md › Launch promotions). Set this to null
 * the day the cap is reached or the window closes, so the claim stays true.
 */
export const LAUNCH_OFFER: LaunchOffer | null = { code: 'FOUNDER', percentOff: 50, households: 200, days: 90 }

/** What Free keeps after the trial. Only what exists. */
export const FREE_FEATURES: readonly string[] = [
  `One shared list, synced live, up to ${FREE_ITEM_LIMIT} items at a time`,
  'Shopping mode, with the list in aisle order',
  'Your saved recipes, to read and cook from',
  `Up to ${MAX_MEMBERS} people`,
]

/** What Plus adds — and what the trial shows. Only what exists. */
export const PLUS_FEATURES: readonly string[] = [
  'Unlimited items',
  'The Garmin watch app: tick things off from your wrist',
  'Recipe import from any link, TikTok, Instagram and YouTube',
  'Meal planning and week templates',
  'Your store learned in walking order',
  'Running low and From before',
  'Stats on your trips and spending',
]

export interface Plan {
  id: PlanId
  label: string
  per: string
}

/** Annual first — the subscribe sheet renders these in order and preselects PLANS[0]. */
export const PLANS: readonly Plan[] = [
  { id: 'annual', label: 'Yearly', per: 'per year' },
  { id: 'monthly', label: 'Monthly', per: 'per month' },
]

export const DEFAULT_PLAN: PlanId = 'annual'

// ── amounts ───────────────────────────────────────────────────────────────────

/** One currency's row of the price table — minor units, as Stripe stores them. */
export interface PlanAmounts {
  annual: number
  monthly: number
  taxInclusive: boolean
}

/** The shape `GET /billing/prices` returns (billing/prices.ts). */
export interface PriceTable {
  base: string
  currencies: Record<string, PlanAmounts>
}

/**
 * Shown only while the prices route can't be reached (offline, or a
 * deployment without the two Price ids). Mirrors the amounts docs/OPERATIONS.md
 * › Stripe billing tells you to create in Stripe — change both together.
 */
export const FALLBACK_PRICES: PriceTable = {
  base: 'usd',
  currencies: {
    usd: { annual: 1499, monthly: 199, taxInclusive: false },
    eur: { annual: 1499, monthly: 199, taxInclusive: false },
    gbp: { annual: 1299, monthly: 179, taxInclusive: false },
    sek: { annual: 14900, monthly: 1900, taxInclusive: false },
  },
}

export function isPriceTable(value: unknown): value is PriceTable {
  const t = value as PriceTable | null
  const base = t?.currencies?.[t.base]
  return typeof base?.annual === 'number' && typeof base?.monthly === 'number'
}

/** The row for a currency, falling back to the table's base so there is always one. */
export function amountsFor(table: PriceTable, currency: string): PlanAmounts {
  return table.currencies[currency] ?? table.currencies[table.base]
}

export function planPrice(plan: PlanId, amounts: PlanAmounts, currency: string, locale: string): string {
  return formatMoney(amounts[plan], currency, locale)
}

/** Sub-label under the selected plan, or null when there's nothing to add. */
export function planNote(plan: PlanId, amounts: PlanAmounts, currency: string, locale: string): string | null {
  if (plan !== 'annual') return null
  // "under $1.30" — the monthly equivalent rounded up to the next ten minor
  // units, so the claim is never an overstatement.
  const perMonth = Math.ceil(amounts.annual / 12 / 10) * 10
  return `Under ${formatMoney(perMonth, currency, locale)} a month`
}

/** One-line summary for places too small for the full selector (Profile). */
export function priceSummary(amounts: PlanAmounts, currency: string, locale: string): string {
  const year = formatMoney(amounts.annual, currency, locale)
  const month = formatMoney(amounts.monthly, currency, locale)
  return `${year} a year, or ${month} a month — for up to ${MAX_MEMBERS} people`
}

/** The yearly price on its own — the number every reason to upgrade quotes. */
export function yearlyPrice(amounts: PlanAmounts, currency: string, locale: string): string {
  return formatMoney(amounts.annual, currency, locale)
}

/**
 * The first year at the founding discount, rounded half up to the minor unit
 * the way Stripe rounds a percentage coupon (£12.99 → £6.50).
 */
export function launchPrice(amounts: PlanAmounts, currency: string, locale: string): string {
  const percent = LAUNCH_OFFER?.percentOff ?? 0
  return formatMoney(Math.round((amounts.annual * (100 - percent)) / 100), currency, locale)
}

/** Shown wherever a price is, since Checkout adds tax we can't compute here. */
export function taxNote(amounts: PlanAmounts): string {
  return amounts.taxInclusive ? 'VAT included. Cancel any time.' : 'Plus VAT where it applies. Cancel any time.'
}

/**
 * Only when the viewer's currency isn't one the Prices carry and the base is
 * being shown instead: Checkout may then present its own conversion.
 */
export const CONVERSION_NOTE = 'Checkout may show this in your local currency.'
